class BeforeYouLeave extends HTMLElement {
  constructor() {
    super();
    this.popup = this.querySelector('.modal--popup');
    this.expiresMinutes = Number(this.dataset.expireMinutes) || 0;
    this.timeToShow = this.expiresMinutes * 60 * 1000;
    this.overlay = this.querySelector('[id^="Modal-Overlay-"]');
    this.content = this.querySelector('.popup__inner');
    this.closeButton = this.querySelector('.popup__close');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.previousActiveElement = null;
    this.closeTimer = null;
    this.labelObserver = new MutationObserver(() => this.updateDialogLabel());

    this.idleTimer = null;
    this.boundReset = this.resetTimer.bind(this);
    this.boundCheck = this.checkIdle.bind(this);
    this.boundDialogKeydown = this.handleDialogKeydown.bind(this);
    this.boundDismissClick = this.handleDismissClick.bind(this);

    this.closeButton?.addEventListener('click', () => this.setClosePopup());
    this.overlay?.addEventListener('click', () => this.setClosePopup());
    this.addEventListener('click', this.boundDismissClick);

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', e => {
        if (e.target.matches('.section-before-you-leave')) this.setOpenPopup();
      });
      document.addEventListener('shopify:section:deselect', e => {
        if (e.target.matches('.section-before-you-leave')) this.setClosePopup();
      });
    }
  }

  connectedCallback() {
    if (!this.popup) return;
    if (this.content) this.labelObserver.observe(this.content, { childList: true, subtree: true });

    if (!Shopify.designMode) {
      ['mousemove', 'keydown', 'scroll'].forEach(evt =>
        document.addEventListener(evt, this.boundReset)
      );
      this.startIdleTimer();
    }
  }

  disconnectedCallback() {
    if (Shopify.designMode) return;
    ['mousemove', 'keydown', 'scroll'].forEach(evt =>
      document.removeEventListener(evt, this.boundReset)
    );
    document.removeEventListener('keydown', this.boundDialogKeydown);
    clearTimeout(this.idleTimer);
    clearTimeout(this.closeTimer);
    this.labelObserver.disconnect();
  }

  startIdleTimer() {
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(this.boundCheck, this.timeToShow);
  }

  resetTimer() {
    clearTimeout(this.idleTimer);
    this.startIdleTimer();
  }

  checkIdle() {
    // Show at most once per browser session so it doesn't repeatedly interrupt
    // (especially disruptive on mobile). Fails open if storage is unavailable.
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem('nc_before_you_leave_shown'); } catch (e) {}
    if (alreadyShown) return;
    if (!document.body.classList.contains('overflow-hidden')) {
      try { sessionStorage.setItem('nc_before_you_leave_shown', '1'); } catch (e) {}
      this.setOpenPopup();
    }
  }

  setOpenPopup() {
    if (!this.popup) return;
    if (this.popup.hasAttribute('open')) {
      this.updateDialogLabel();
      return;
    }
    clearTimeout(this.closeTimer);
    this.previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.popup.setAttribute('open', 'true');
    this.popup.setAttribute('aria-hidden', 'false');
    this.updateDialogLabel();
    this.animateOpen();
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');
    document.addEventListener('keydown', this.boundDialogKeydown);
    requestAnimationFrame(() => this.closeButton?.focus({ preventScroll: true }));
  }

  setClosePopup() {
    if (!this.popup) return;
    this.animateClose();
    this.startIdleTimer();
    document.removeEventListener('keydown', this.boundDialogKeydown);
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.popup.removeAttribute('open');
      this.popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.documentElement.removeAttribute('scroll-lock');
      this.previousActiveElement?.focus?.({ preventScroll: true });
    }, this.reducedMotion.matches ? 0 : 220);
  }

  updateDialogLabel() {
    const heading = this.content?.querySelector(
      '.before-you-leave-content :is(h1, h2, h3), .before-you-leave-content .text-block:first-child p'
    );
    if (!heading) return;
    if (!heading.id) heading.id = 'BeforeYouLeave-Heading';
    this.popup.setAttribute('aria-labelledby', heading.id);
    this.popup.removeAttribute('aria-label');
    this.prepareDismissButton();
  }

  prepareDismissButton() {
    const button = this.content?.querySelector(
      '.before-you-leave-content > .section-content-wrapper > .button:not([href])'
    );
    if (!button) return;
    button.dataset.beforeYouLeaveDismiss = 'true';
    button.setAttribute('role', 'button');
    button.setAttribute('tabindex', '0');
    button.removeAttribute('aria-disabled');
  }

  handleDismissClick(event) {
    if (!event.target.closest?.('[data-before-you-leave-dismiss]')) return;
    event.preventDefault();
    this.setClosePopup();
  }

  handleDialogKeydown(event) {
    if (!this.popup?.hasAttribute('open')) return;
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      event.target.closest?.('[data-before-you-leave-dismiss]')
    ) {
      event.preventDefault();
      this.setClosePopup();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.setClosePopup();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      this.popup.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) {
      event.preventDefault();
      this.popup.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  animateOpen() {
    if (this.reducedMotion.matches || !window.Motion?.timeline) return;
    window.Motion.timeline([
      [this.content, { opacity: [0, 1], transform: ['translate(-50%, -40%)', 'translate(-50%, -50%)'] }, { duration: 0.3, at: '-0.15' }]
    ]).finished;
  }

  animateClose() {
    if (this.reducedMotion.matches || !window.Motion?.timeline) return;
    window.Motion.timeline([
      [this.content, { opacity: [1, 0], transform: ['translate(-50%, -50%)', 'translate(-50%, -40%)'] }, { duration: 0.2 }]
    ]).finished;
  }
}
if (!customElements.get('before-you-leave')) customElements.define('before-you-leave', BeforeYouLeave);

class CopyCode extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.tooltip = this.querySelector('.tooltip');
    this.resetTooltipTimer = null;
    this.input = this.querySelector('input');
    this.resetInputTimer = null;
  }
  
  connectedCallback() {
    if (this.button) {
      this.button.addEventListener('click', this.copyCode.bind(this));
    }
  }

  copyCode() {
    const text = this.getAttribute('data-text');
    if (!text) return;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }

    this.showSuccess();
  }

  showSuccess() {
    const defaultMessage = window.theme?.strings?.copy || 'Copy';
    const defaultInputValue = this.input?.value ?? '';

    if (this.tooltip && this.tooltip.dataset.success_message) {
      this.tooltip.textContent = this.tooltip.dataset.success_message;
      clearTimeout(this.resetTooltipTimer);
      this.resetTooltipTimer = setTimeout(() => {
        this.tooltip.textContent = defaultMessage;
      }, 1500);
    }

    if (this.input) {
      this.input.value = 'Copied!';
      clearTimeout(this.resetInputTimer);
      this.resetInputTimer = setTimeout(() => {
        this.input.value = defaultInputValue;
      }, 1500);
    }

    this.classList.add('copied');
    setTimeout(() => {
      this.classList.remove('copied');
    }, 1500);
  }
}
if (!customElements.get('copy-code')) customElements.define('copy-code', CopyCode);
